'use client'

import {
  Button,
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  CMSLink,
} from '@/components'
import { Menu } from '@/payload-types'
import { Menu as MenuIcon } from 'lucide-react'
import React, { type ReactElement, useState } from 'react'
import { useIsMobile } from '@/hooks/use-mobile'

interface GlobalNavProps {
  nav: Menu
}

function GlobalNav({ nav }: Readonly<GlobalNavProps>): ReactElement {
  const [open, setOpen] = useState(false)
  const isMobile = useIsMobile()

  if (isMobile) {
    return (
      <TooltipProvider>
        <Sheet open={open} onOpenChange={setOpen}>
          <Tooltip>
            <TooltipTrigger asChild>
              <SheetTrigger className="text-muted-foreground" asChild>
                <Button size="icon" variant="outline" aria-label="Menu" className="rounded-full">
                  <MenuIcon strokeWidth={1} />
                </Button>
              </SheetTrigger>
            </TooltipTrigger>
            <TooltipContent>Menu</TooltipContent>
          </Tooltip>
          <SheetContent className="flex flex-col min-w-full min-h-full" side="top">
            <SheetHeader>
              <SheetTitle className="sr-only">Menu</SheetTitle>
            </SheetHeader>

            <ul className="flex flex-col list-none gap-2 p-8">
              {nav.items.map(({ id, link }) => {
                return (
                  <li key={id}>
                    <CMSLink {...link} appearance="inline" />
                  </li>
                )
              })}
            </ul>
          </SheetContent>
        </Sheet>
      </TooltipProvider>
    )
  }

  return (
    <TooltipProvider>
      <NavigationMenu className="hidden md:block">
        <NavigationMenuList className="p-0.5 rounded-full">
          {nav.items.map(({ id, link }) => {
            return (
              <NavigationMenuItem key={id}>
                <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
                  <CMSLink {...link} appearance="inline" />
                </NavigationMenuLink>
              </NavigationMenuItem>
            )
          })}
        </NavigationMenuList>
      </NavigationMenu>
    </TooltipProvider>
  )
}

export { GlobalNav, type GlobalNavProps }
